import { useEffect, useRef, type ReactNode } from 'react'

interface Props {
  /**
   * `id` of the heading inside `children` that names this dialog. Required
   * rather than optional: a dialog with no accessible name is announced as
   * just "dialog", which tells a screen-reader user nothing about where they
   * have landed.
   */
  labelledBy: string
  /** Called for every way out — Close, Cancel, or Esc. */
  onClose: () => void
  children: ReactNode
}

/**
 * A modal built on the native `<dialog>` element.
 *
 * `showModal()` is what makes this a modal rather than a styled div: the
 * browser puts the dialog in the top layer (so no z-index can cover it), makes
 * the rest of the page inert, keeps Tab inside it, and wires up Esc — all
 * behaviour that a hand-rolled overlay has to reimplement and usually gets
 * subtly wrong.
 *
 * Deliberately does not close on a backdrop click. Everything shown in one of
 * these is a form, and a stray tap outside it on a phone would throw away
 * whatever had been typed. Closing stays an explicit act: the Close button,
 * Cancel, or Esc.
 */
export function Modal({ labelledBy, onClose, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (dialog === null) return

    if (!dialog.open) dialog.showModal()

    // The top layer covers the page but does not stop it scrolling underneath,
    // which on a phone leaves the list sliding around behind the form.
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={labelledBy}
      // Fires for Esc and for close() alike, so every exit lands here and the
      // owner never has to track how the dialog was dismissed.
      onClose={onClose}
    >
      {children}
    </dialog>
  )
}
